/**
 * Top-level router (createBrowserRouter, data-router mode).
 *
 * Public auth routes are outside <AuthGate>; everything else is gated
 * behind <AuthGate> → <AppLayout>. This mirrors saas-admin's router shape
 * exactly, adapted for the wsr-product-admin product-admin console section routes.
 *
 * Section routes (WSRPDM-0011) originally rendered shared placeholder content;
 * screen specs WSRPDM-0012–0017 have since replaced every element with real
 * content (Dashboard was the last one in WSRPDM-0017):
 *   /dashboard      → Dashboard        (WSRPDM-0017)
 *   /queue          → Queue            (WSRPDM-0013)
 *   /notifications  → Notifications    (WSRPDM-0015)
 *   /tenants        → Tenants          (WSRPDM-0012/0018) — wsr-api half shipped, saas-auth identity half (name/create/users) pending
 *   /engine         → Engine config    (WSRPDM-0014)
 */
import { Navigate, createBrowserRouter, type RouteObject } from 'react-router-dom';
import { RootLayout } from '@/routes/RootLayout';
import { AppLayout } from '@/components/AppLayout';
import { AuthGate } from '@/auth/AuthGate';
import { Forbidden } from '@/routes/Forbidden';
import { NotFound } from '@/routes/NotFound';
import { RouteError } from '@/routes/RouteError';
import { SignedOut } from '@/routes/SignedOut';
import { AuthError } from '@/routes/AuthError';
import { AuthReturn } from '@/routes/AuthReturn';
import { NoAccess } from '@/routes/NoAccess';
import { Dashboard } from '@/routes/Dashboard';
import { Tenants } from '@/routes/Tenants';
import { TenantDetailPage } from '@/routes/tenants/TenantDetailPage';
import { Engine } from '@/routes/Engine';
import { Queue } from '@/routes/Queue';
import { Notifications } from '@/routes/Notifications';
import { GlobalConfig } from '@/routes/GlobalConfig';

const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      // Public auth routes — no AuthGate.
      { path: 'auth/return', element: <AuthReturn /> },
      { path: 'auth/no-access', element: <NoAccess /> },
      { path: 'auth/signed-out', element: <SignedOut /> },
      { path: 'auth/error', element: <AuthError /> },

      // Everything else is gated behind AuthGate → AppLayout.
      {
        element: <AuthGate />,
        children: [
          {
            element: <AppLayout />,
            children: [
              // Default: redirect root to /dashboard.
              { index: true, element: <Navigate to="/dashboard" replace /> },

              // Console section routes.
              { path: 'dashboard', element: <Dashboard /> },
              { path: 'queue', element: <Queue /> },
              { path: 'notifications', element: <Notifications /> },
              { path: 'tenants', element: <Tenants /> },
              { path: 'tenants/:tenantId', element: <TenantDetailPage /> },
              { path: 'engine', element: <Engine /> },
              { path: 'global-config', element: <GlobalConfig /> },

              // Platform-admin gate hit explicitly or by API 403.
              { path: 'forbidden', element: <Forbidden /> },

              { path: '*', element: <NotFound /> },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
